import type { MatchSettings } from "../../shared/game-types.js";
import { audioStore, DEFAULTS } from "./tuning.js";
import { createStore, useStore } from "./store.js";

/**
 * Host settings, as opposed to tuning.
 *
 * The tuner edits files on disk and only exists in development. These are the
 * handful of choices a person setting up a game actually makes, so they live in
 * this browser's storage and travel to the server when the room is opened.
 */
export interface GameSettings extends Required<MatchSettings> {
  soundOn: boolean;
}

const KEY = "factfeud:settings";

export const DEFAULT_SETTINGS: GameSettings = {
  rounds: DEFAULTS.rules.roundsPerMatch,
  answerSeconds: DEFAULTS.rules.answerSeconds,
  strikeLimit: DEFAULTS.rules.strikeLimit,
  maxDifficulty: DEFAULTS.rules.maxDifficulty,
  excludedThemes: [],
  soundOn: DEFAULTS.audio.master.enabled,
};

function read(): GameSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const stored = JSON.parse(raw) as Partial<GameSettings>;
    // Merged onto the defaults rather than trusted wholesale: a settings blob
    // written by an older build is missing whatever has been added since.
    return {
      ...DEFAULT_SETTINGS,
      ...stored,
      excludedThemes: Array.isArray(stored.excludedThemes)
        ? stored.excludedThemes
        : [],
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export const settingsStore = createStore<GameSettings>(read());

export const useSettings = () => useStore(settingsStore);

export function updateSettings(patch: Partial<GameSettings>): void {
  const next = { ...settingsStore.get(), ...patch };
  settingsStore.set(next);
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Private browsing, quota, a locked-down kiosk — the setting still applies
    // for this session, it just will not survive a reload. Not worth failing.
  }
  applySound(next);
}

export function resetSettings(): void {
  updateSettings({ ...DEFAULT_SETTINGS });
}

/**
 * Sound is the one setting that acts on this device rather than on the match,
 * so it is pushed straight into the audio store. Set directly, never through
 * `updateTuning`, which would try to write audio.tuning.json to disk.
 */
export function applySound(settings: GameSettings): void {
  const audio = audioStore.get();
  if (audio.master.enabled === settings.soundOn) return;
  audioStore.set({
    ...audio,
    master: { ...audio.master, enabled: settings.soundOn },
  });
}

/** Just the parts the server needs to open a room. */
export function matchSettings(settings: GameSettings): MatchSettings {
  return {
    rounds: settings.rounds,
    answerSeconds: settings.answerSeconds,
    strikeLimit: settings.strikeLimit,
    maxDifficulty: settings.maxDifficulty,
    excludedThemes: settings.excludedThemes,
  };
}
